import { useState } from "react";
import EmailDialog from "../../../components/Approval/EmailDialog";
import { useApiClient } from "../../../../data/clients/ApiClient";
import { Design } from "../../../../core/models/entities/Design";
import { PageQuery } from "../../../../utils/models/pageQuery";
import { Organization } from "../../../../core/models/entities/Organization";
import { QueryChanger } from "../../../shared/hooks/usePageQuery";
import { TableData } from "../../../components/GenericTable/FunctionalList";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { useNavigate } from "react-router-dom";
import { PrimaryButton } from "../../../components/Buttons/PrimaryButton";
import { designColumnsConfig } from "../functions/designColumnsConfig";
import { DesignRowItem } from "../components/design-list-item.component";
import { Profile } from "../../../../core/models/entities/Profile";
import {
  FilterRecipe,
  FilterChangeEvent,
} from "../../../components/GenericTable/SubComponents/TableHeader/ListHeader";
import { Role } from "../../../../core/models/entities/Role";
import { organizationFilterField } from "../../../shared/const/organizationFilterField";
import { Manufacturer } from "../../../../core/models/entities/Manufacturer";
import { Style } from "../../../../core/models/entities/Style";
import {
  TransferLogoType,
  TransferMethod,
} from "../../../../core/models/entities/LogoOptions";
import {
  GetOverallApprovalStatus,
  LogoState,
} from "../../../../core/models/entities/BaseLogo";
import { ApprovalStatus } from "../../../../core/models/entities/Approval";
import { DesignStatus } from "../../../../core/models/entities/enums/DesignStatus";
import { useDesignActionHandler } from "../hooks/useDesignActionHandler";
import { useMultiSelectStateMachine } from "../../../shared/hooks/useMultiSelectStateMachine";
import { useLogoContext } from "../../design-editor2/contexts/logo-options.context";
import { AddToCartIcon } from "../../../../resources/icons/Icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faPlus,
  faShare,
  faEnvelope,
} from "@fortawesome/free-solid-svg-icons";
import { useErrorToast, withToasts } from "../../../components/Toast/Toast";
import { SecondaryButton } from "../../../components/Buttons/SecondaryButton";
import { LogoPdfGenerator } from "../../../../utils/functions/pdf-generator.service";
import { Menu, MenuItem } from "@mui/material";

export interface DesignRow extends Design {
  checked: boolean | undefined;
}

export type DesignClickCallback = (design: Design) => void;

export function useFunctionalListDesignViewModel(
  onSelectDesign: DesignClickCallback,
  onApprove: DesignClickCallback,
  onReject: DesignClickCallback,
  lastPage: number,
  query: PageQuery,
  customers: Organization[],
  isPageLoading: boolean,
  vendors: Organization[] | undefined,
  queryChanger: QueryChanger,
  manufacturers: Manufacturer[],
  styles: Style[],
  methods: TransferMethod[],
  onShowRejectionNote: DesignClickCallback,
  // openInitApproval: (design: Design) => void,
  designs?: Design[],
  updatingDesignId?: number,
  loadingPrices?: boolean
): TableData<Design> {
  const { profile, role } = useProfileContext();
  const {
    handleAddToCart,
    handleAddItemsToCart,
    handleDeleteDesign,
    handlePlaceVerdict,
    handleGoToUpdateDesign,
    handleRejectAndDelete,
  } = useDesignActionHandler();
  const { generatePublicKeys, sendMultiApprovalEmail, ...apiClient } =
    useApiClient();
  const [deleted, setDeleted] = useState<number[]>([]);

  const navigate = useNavigate();

  function onToCreateDesign() {
    navigate("/design/create");
  }

  const { selectedData, onRowChecked, onMultiSelectRow } =
    useMultiSelectStateMachine<Design>({
      onSelect: onSelectDesign,
    });

  // Email dialog state for multi-select Send Preview
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [approvalEmail, setApprovalEmail] = useState("");

  // Loading state for PDF download
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  // Loading state for sending designs via email
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);

  function handleShareClick(event: React.MouseEvent<HTMLElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleShareClose() {
    setAnchorEl(null);
  }

  // Validation function to check if user can perform bulk actions with current selection
  function validateSelectionCount(
    selectedCount: number,
    actionName: string
  ): boolean {
    if (role !== Role.Admin && selectedCount > 5) {
      useErrorToast(
        `You can only ${actionName} up to 5 items at once. Please select fewer items.`
      );
      return false;
    }
    return true;
  }

  function addToCart() {
    handleAddItemsToCart(Object.values(selectedData), 10);
  }

  function sendPreview() {
    handleShareClose();
    const selectedCount = Object.values(selectedData)?.length || 0;
    if (!validateSelectionCount(selectedCount, "send") || isSendingEmail) {
      return;
    }
    setEmailDialogOpen(true);
  }

  async function handleDownloadPdf() {
    handleShareClose();
    const selectedCount = Object.values(selectedData)?.length || 0;
    if (
      !validateSelectionCount(selectedCount, "download") ||
      isDownloadingPdf
    ) {
      return;
    }

    setIsDownloadingPdf(true);
    const selectedDesigns = Object.values<Design>(selectedData);
    const vendorLogoUrl = profile?.whiteLabelConfigurations?.logoUrl;

    async function transaction() {
      return await LogoPdfGenerator.generateMultipleDesignsPdf(
        selectedDesigns,
        vendorLogoUrl,
        apiClient
      );
    }

    try {
      await withToasts(
        transaction,
        "Generating design PDF. This might take a while...",
        "Design PDF downloaded successfully",
        "Failed to generate design PDF"
      );
    } catch (error) {
      // Error is already handled by withToasts
      console.error("Failed to generate design PDF:", error);
    } finally {
      setIsDownloadingPdf(false);
    }
  }

  async function handleSendPreviewWithEmail() {
    const selectedDesigns = Object.values<Design>(selectedData);

    setIsSendingEmail(true);

    async function transaction() {
      // Generate public keys for each selected design using API
      const response = await generatePublicKeys(
        undefined,
        selectedDesigns.map((d) => d.id)
      );
      const keysParam = response.publicKeys.join(",");
      const url = `${window.location.origin}/external-approval/designs?keys=${keysParam}`;

      // Send email with preview URL
      await sendMultiApprovalEmail(
        response.publicKeys,
        "design",
        approvalEmail
      );

      return response;
    }

    try {
      await withToasts(
        transaction,
        "Sending email with designs",
        "Emails sent successfully",
        "Failed to send email with designs"
      );
      setEmailDialogOpen(false);
    } catch (error) {
      // Error is already handled by withToasts
      console.error("Failed to send preview email:", error);
    } finally {
      setIsSendingEmail(false);
    }
  }

  const center = undefined;

  const selectedCount = Object.values(selectedData)?.length || 0;
  const right = (
    <div className="flex space-x-2">
      <PrimaryButton
        key={"design-create"}
        width={"md"}
        className={"px-4"}
        onClick={onToCreateDesign}
      >
        <FontAwesomeIcon icon={faPlus} />
        <span className="flex-1">Create new Design</span>
      </PrimaryButton>
      {role !== Role.Admin && (
        <PrimaryButton
          width={"md"}
          className={"px-4"}
          onClick={addToCart}
          disabled={selectedCount === 0}
        >
          <AddToCartIcon />
          <span className={"flex-1"}>Add to Order ({selectedCount})</span>
        </PrimaryButton>
      )}
      <SecondaryButton
        width={
          isDownloadingPdf || isSendingEmail
            ? "sm"
            : selectedCount > 0
            ? "xs"
            : "icon"
        }
        className={"px-4"}
        onClick={handleShareClick}
        disabled={selectedCount === 0 || isDownloadingPdf || isSendingEmail}
      >
        <FontAwesomeIcon icon={faShare} />
        <span className={"flex-1"}>
          {isDownloadingPdf
            ? "Generating..."
            : isSendingEmail
            ? "Sending..."
            : selectedCount > 0
            ? `(${selectedCount})`
            : ""}
        </span>
      </SecondaryButton>
      <Menu
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleShareClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleDownloadPdf} disabled={isDownloadingPdf}>
          <FontAwesomeIcon icon={faDownload} className="mr-2 w-4" />
          Download PDF
        </MenuItem>
        <MenuItem onClick={sendPreview} disabled={isSendingEmail}>
          <FontAwesomeIcon icon={faEnvelope} className="mr-2 w-4" />
          Send via Email
        </MenuItem>
      </Menu>
    </div>
  );

  // Because the entire roundabout for designs doesn't start and end the same place,
  //  we cannot simply ask for a fresh list of designs when deleting one.
  // Instead we use a hacky list of deleted designs to ensure we don't remove designs that were not deleted.
  // Next time the list is rendered, the deleted designs will not be included.
  const handleDeleteAndHide = async (design: Design) => {
    try {
      await handleDeleteDesign(design);
      setDeleted([...deleted, design.id]);
    } catch {
      // Do not mark design as deleted after all.
    }
  };

  function isDesignDisabled(design: Design): boolean {
    return design.status === DesignStatus.Draft;
  }

  const handleFilterChange = (event: FilterChangeEvent) => {
    if (profile?.userOrganizationInformations?.role === Role.Vendor) {
      const showOnlyOwned = event.activeFilters.find(
        (f) => f.key === "showOnlyOwned"
      );
      const vendorOrgId =
        profile.userOrganizationInformations.organizationId.toString();

      let newFilters = [...event.activeFilters];

      if (showOnlyOwned && showOnlyOwned.value === true) {
        newFilters = newFilters.filter(
          (f) =>
            f.key !== "showOnlyOwned" &&
            f.key !== organizationFilterField &&
            f.key !== "mainOrganizationId"
        );
        newFilters.push({
          key: "mainOrganizationId",
          value: vendorOrgId,
        });
        queryChanger.handleFilterChange({ activeFilters: newFilters });
        return;
      } else {
        newFilters = newFilters.filter((f) => f.key !== "showOnlyOwned");
        newFilters = newFilters.filter(
          (f) => !(f.key === "mainOrganizationId" && f.value === vendorOrgId)
        );
        queryChanger.handleFilterChange({ activeFilters: newFilters });
        return;
      }
    }
    queryChanger.handleFilterChange(event);
  };

  return {
    tableTitle: "Design",
    columns: designColumnsConfig(role === Role.Admin),
    onRowClick: onMultiSelectRow,
    lastPage: lastPage,
    query: query,
    isLoading: isPageLoading,
    onNewPage: queryChanger.handleNewPage,
    onNewSort: queryChanger.handleSortByField,
    onFilterChange: handleFilterChange,
    onSearchQueryChange: queryChanger.handleSearchQueryChange,
    onPageSizeChange: queryChanger.handlePageSizeChange,
    renderRow: (columns, data) => (
      <DesignRowItem
        design={data}
        columns={columns}
        onSeeRejectNote={() => onShowRejectionNote(data)}
        onInitiateApproval={() => {}}
        onAddToCart={handleAddToCart}
        onPlaceVerdict={handlePlaceVerdict}
        onDeleteDesign={handleDeleteAndHide}
        onEditDesign={handleGoToUpdateDesign}
        onRejectAndDelete={handleRejectAndDelete}
        onApprove={onApprove}
        onReject={onReject}
        updatingDesignId={updatingDesignId}
        loadingPrices={loadingPrices}
      />
    ),
    getKey: getKey,
    center: center,
    right: right,
    data: designs?.filter((d) => !deleted.includes(d.id)),
    getRowColor: logoRowBgColor,
    filters: buildDesignFilterRecipes(
      profile!,
      methods,
      manufacturers,
      styles,
      customers,
      vendors
    ),
    multiSelect: true,
    selectedData: selectedData,
    onRowSelected: onRowChecked,
    isRowDisabled: isDesignDisabled,
    showVendorFilter: true,
    customers: customers,
    component: (
      <EmailDialog
        open={emailDialogOpen}
        setOpen={setEmailDialogOpen}
        email={approvalEmail}
        onEmailChange={setApprovalEmail}
        onSubmit={handleSendPreviewWithEmail}
      />
    ),
  };
}

function logoRowBgColor(design: Design) {
  if (design.status === DesignStatus.Draft) {
    return "bg-row-draft";
  }

  switch (GetOverallApprovalStatus(design.approval!)) {
    case ApprovalStatus.Rejected:
      return "bg-row-warn";
    case ApprovalStatus.Approved:
      return "bg-row-standard";
    case ApprovalStatus.Pending:
      return "bg-row-pending";
  }
}

function buildDesignFilterRecipes(
  profile: Profile,
  transferMethods: TransferMethod[],
  manufacturers: Manufacturer[],
  style: Style[],
  customers?: Organization[],
  vendor?: Organization[]
) {
  const { options } = useLogoContext();
  const filters: FilterRecipe<any>[] = [];

  if (profile.userOrganizationInformations.role >= Role.Admin && vendor) {
    filters.push({
      options: vendor,
      key: organizationFilterField,
      getOptionValue: (org) => org.id,
      getOptionDisplay: (org) => org.name,
      defaultTitle: "Vendors",
    } as FilterRecipe<Organization>);
  }

  if (profile.userOrganizationInformations.role >= Role.Vendor && customers) {
    /*filters.push({
      options: [
        'N/A'
      ],
      key: 'ownership',
      getOptionValue: (o => o),
      getOptionDisplay: (o => o),
      defaultTitle: 'Ownership'
    } as FilterRecipe<string>)*/

    filters.push({
      options: customers,
      key: organizationFilterField,
      getOptionValue: (org) => org.id,
      getOptionDisplay: (org) => org.name,
      defaultTitle: "Customer",
    } as FilterRecipe<Organization>);
  }

  filters.push({
    options: [
      LogoState.Approved,
      LogoState.Pending,
      LogoState.Rejected,
      LogoState.Draft,
    ],
    key: "stateFilter",
    getOptionValue: (org) => org as string,
    getOptionDisplay: (org) => org as string,
    defaultTitle: "Status",
  } as FilterRecipe<LogoState>);

  filters.push({
    options: options.transferLogoTypes,
    key: "typeFilter",
    getOptionValue: (o) => o.typeKey,
    getOptionDisplay: (o) => o.nickname,
    defaultTitle: "Type",
  } as FilterRecipe<TransferLogoType>);

  filters.push({
    options: transferMethods,
    key: "methodFilter",
    getOptionValue: (org) => ":" + org.key + ":",
    getOptionDisplay: (org) => org.displayName,
    defaultTitle: "Method",
  } as FilterRecipe<TransferMethod>);

  /* Manufacturer and Model filters don't work with current data structure - needs updating to work.
  filters.push({
    options: manufacturers,
    key: "style.manufacturerId",
    getOptionValue: (org) => org.id,
    getOptionDisplay: (org) => org.name,
    defaultTitle: "Manufacturer",
  } as FilterRecipe<Manufacturer>);

  filters.push({
    options: style,
    key: "style.id",
    getOptionValue: (o) => o.id,
    getOptionDisplay: (o) => o.name + " - " + o.model,
    defaultTitle: "Model",
  } as FilterRecipe<Style>);*/

  return filters;
}

const getKey = (data: Design) => data.id;
